import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Card, List, Typography, Button, Modal, Spin } from "antd";
import { DeleteOutlined, UserOutlined } from "@ant-design/icons";
import { ContentTable, SearchBar } from "@authzsvc/common-react-components";
import { showSuccessNotification } from "../../../util";
import TooltipIcon from "../../common/TooltipIcon";

const GroupMembersView = ({
  searchValue,
  setSearchValue,
  columns,
  dataSource,
  total,
  extraButtons,
  loading,
  onDelete,
  getMembers,
  disabled,
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [sortedDataSource, setSortedDataSource] = useState(dataSource);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState([]);
  const [totalLabel, setTotalLabel] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const sortDataSource = (data) => {
    return data.sort((a, b) => {
      const nameA = a.memberIdentity
        ? a.memberIdentity.displayName.toLowerCase()
        : a.memberGroup.displayName.toLowerCase();
      const nameB = b.memberIdentity
        ? b.memberIdentity.displayName.toLowerCase()
        : b.memberGroup.displayName.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  useEffect(() => {
    const sortedData = sortDataSource([...dataSource]);
    setSortedDataSource(sortedData);
    const label = searchValue
      ? ` (Total Members Found: ${sortedData.length})`
      : ` (Total Members: ${total})`;
    setTotalLabel(label);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, total]);

  const showDeleteModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setIsModalOpen(false);
    setLoadingDelete(true);
    await onDelete(selectedRows);
    setSelectedRows([]);
    setSelectedDetails([]);
    setLoadingDelete(false);

    showSuccessNotification("Members removed successfully!", "");
  }, [onDelete, selectedRows]);

  const handleCancelDelete = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Memoize the callback to prevent rowSelection recreation
  const onRowSelection = useCallback(
    (selectedRowKeys) => {
      const selectedDetails = selectedRowKeys.map((key) => {
        const row = dataSource.find(
          (item) =>
            (item.memberIdentity && item.memberIdentity.id === key) ||
            (item.memberGroup && item.memberGroup.id === key)
        );

        return {
          id: row.memberIdentity ? row.memberIdentity.id : row.memberGroup.id,
          type: row.memberIdentity ? "identity" : "group",
          displayName: row.memberIdentity
            ? `${row.memberIdentity.displayName} (${
                row.memberIdentity.upn || "pending confirmation"
              })`
            : row.memberGroup.groupIdentifier,
        };
      });

      setSelectedRows(selectedDetails);
      setSelectedDetails(selectedDetails.map((detail) => detail.displayName));
    },
    [dataSource]
  );

  // Memoize rowSelection object - THIS IS CRITICAL
  const rowSelection = useMemo(
    () => ({
      onChange: onRowSelection,
      getCheckboxProps: (record) => ({
        name: record.displayName,
        "aria-label": `Select member ${record.displayName || record.name}`,
      }),
    }),
    [onRowSelection]
  );

  // Memoize getRowKey
  const getRowKey = useCallback((record) => {
    if (record.memberIdentity) {
      return record.memberIdentity.id;
    } else if (record.memberGroup) {
      return record.memberGroup.id;
    }
  }, []);

  // Memoize pagination - THIS IS ALSO CRITICAL
  const paginationConfig = useMemo(
    () => ({
      current: currentPage,
      onChange: (page) => setCurrentPage(page),
      showSizeChanger: true,
      hideOnSinglePage: false,
      pageSizeOptions: ["20", "50", "100"],
      defaultPageSize: 20,
    }),
    [currentPage]
  );

  const hasSelections = selectedRows.length > 0;

  // Optimize modal content - show count for large selections
  const renderModalContent = useMemo(() => {
    if (selectedDetails.length > 100) {
      return (
        <p>
          Are you sure you want to remove{" "}
          <strong>{selectedDetails.length}</strong> members?
        </p>
      );
    }

    return (
      <>
        <p>
          Are you sure you want to remove{" "}
          <strong>{selectedDetails.length}</strong> members?
        </p>
        <List
          dataSource={selectedDetails}
          renderItem={(item) => <List.Item>{item}</List.Item>}
          bordered
          size="small"
          style={{ maxHeight: 200, overflow: "auto" }}
        />
      </>
    );
  }, [selectedDetails]);

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          <UserOutlined style={{ marginRight: 8, color: "#bfbfbf" }} />
          Group members
          <Typography.Text
            type="secondary"
            style={{ marginLeft: 8, fontWeight: "normal", fontSize: "inherit" }}
          >
            {totalLabel}
          </Typography.Text>
          <TooltipIcon
            size={16}
            title="Dynamic members are calculated whenever the criteria are changed and periodically. If you experience a short delay this is likely because the queue is busy and should be resolved within a few minutes."
          />
        </Typography.Title>
      }
      extra={extraButtons}
    >
      <div style={{ marginBottom: "16px" }}>
        <SearchBar
          onSearch={getMembers}
          searchValue={searchValue}
          onSearchValueChange={setSearchValue}
          filter={true}
          filterPlacement="before"
          allowClear={true}
        />
      </div>
      <Spin spinning={loading}>
        <ContentTable
          loading={loading}
          rowKey={getRowKey}
          pagination={paginationConfig}
          columns={columns}
          dataSource={sortedDataSource}
          rowSelection={rowSelection}
        />
        <div
          style={{
            marginTop: sortedDataSource.length === 0 ? "16px" : "-48px",
          }}
        >
          <Button
            id="deleteSelectionsButton"
            loading={loadingDelete}
            disabled={!hasSelections || disabled}
            onClick={showDeleteModal}
            danger
            type="primary"
            icon={<DeleteOutlined />}
          />
        </div>
        {/* Render modal outside of table context and only when needed */}
        {isModalOpen && (
          <Modal
            title={
              <span>
                <DeleteOutlined style={{ marginRight: 8, color: "#ff4d4f" }} />
                Confirm Deletion
              </span>
            }
            open={true}
            centered
            onCancel={handleCancelDelete}
            onOk={handleConfirmDelete}
            okButtonProps={{ danger: true }}
            destroyOnHidden={true}
          >
            {renderModalContent}
          </Modal>
        )}
      </Spin>
    </Card>
  );
};

GroupMembersView.propTypes = {
  searchValue: PropTypes.string.isRequired,
  setSearchValue: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  dataSource: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  extraButtons: PropTypes.node,
  loading: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  getMembers: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default GroupMembersView;
